<script setup>
import { computed } from 'vue'
import { useQueueStore } from '@state/stores/queue'
import { usePlaybackStore } from '@state/stores/playback'
import DataLayer from '@services/data-layer/index'
import modalService from '@/services/modal-service'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useMessages } from '@composables/use-messages'
import { useQueueService } from '@composables/use-queue-service'

const props = defineProps({
    imageResource: {
        type: [Object, Boolean],
        default: false,
    },
    imageType: {
        type: String,
        default: 'album_art',
    },
    mediaResource: {
        type: [Object, Boolean],
        default: false,
    },
    mediaType: {
        type: [String, Boolean],
        default: false,
    },
    fill: {
        type: Boolean,
        default: true,
    },
    centered: {
        type: Boolean,
        default: false,
    },
    radius: {
        type: String,
        default: 'none',
        validator: value => ['none', 'list', 'card'].includes(value),
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const playbackStore = usePlaybackStore()
const getMessageText = useMessages()
const queueService = useQueueService()

const hasArt = computed(
    () => props.imageResource && props.imageResource[props.imageType] && props.imageResource[props.imageType].url
)

const playbackStatus = computed(() => playbackStore.status)

const isMediaLoaded = computed(
    () =>
        playbackStore.resource &&
        (props.mediaResource.id === playbackStore.resource.id ||
            props.mediaResource.id === queueStore.playlistId) &&
        props.mediaType === playbackStore.resource.type
)

function playMix() {
    queueService.playMix(props.mediaResource).catch(err => {
        console.error(err)
    })
}

function playChannel() {
    queueService.playRadioChannel(props.mediaResource.id)
}

function playPlaylist(position = 0) {
    DataLayer.playlists
        .getRevisionsMixes(props.mediaResource.id, props.mediaResource.mixes_count || 100, 0)
        .then(res => {
            const mixes = res.body.map(revisionsMixes => revisionsMixes.mixes)
            queueService.playMixMultiple({ mixes, id: props.mediaResource.id, position })
        })
        .catch(err => {
            console.error(err)
            appearanceStore.handleNotification({
                message: getMessageText('SOMETHING_WENT_WRONG'),
                type: 'warning',
            })
        })
}

function playMedia() {
    if (!authStore.loggedIn) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }
    if (isMediaLoaded.value) {
        return queueService.pause()
    }
    if (props.mediaType === 'mix') {
        playMix()
    } else if (props.mediaType === 'playlist') {
        playPlaylist()
    } else if (props.mediaType === 'channel') {
        playChannel()
    }
}
</script>

<template>
    <div
        :class="[
            'base-resource-cover',
            isMediaLoaded && (playbackStatus === 'playing' || playbackStatus === 'loading') && 'base-resource-cover-playing',
            { 'base-resource-cover-fill': hasArt && fill },
            { 'base-resource-cover-centered': centered },
            { 'base-resource-cover-radius-list': radius === 'list' },
            { 'base-resource-cover-radius-card': radius === 'card' },
        ]"
    >
        <div
            v-if="mediaResource"
            class="icon-container"
        >
            <div
                class="action"
                @click.prevent="() => {}"
            >
                <div v-if="$slots.primaryActions">
                    <BaseDropdown :padded="false">
                        <template #trigger>
                            <BaseButton
                                :modifiers="{ autoWidth: true, transparent: true }"
                                button-type="tag"
                                aria-label="Options"
                                size="small"
                            />
                        </template>
                        <template #dropdown>
                            <div>
                                <slot name="primaryActions" />
                            </div>
                        </template>
                    </BaseDropdown>
                </div>
            </div>
            <div @click.prevent="playMedia">
                <BaseIcon
                    v-if="isMediaLoaded && playbackStatus === 'playing'"
                    class="icon-play"
                    name="pause"
                />
                <BaseIcon
                    v-else-if="isMediaLoaded && playbackStatus === 'loading'"
                    :class="['icon-play', 'icon-spinning']"
                    name="spinner"
                />
                <span
                    v-else
                    class="icon-play play-disc"
                >
                    <BaseIcon name="play" />
                </span>
            </div>
            <div
                class="action"
                @click.prevent="() => {}"
            >
                <div v-if="$slots.secondaryActions">
                    <BaseDropdown :padded="false">
                        <template #trigger>
                            <BaseButton
                                :modifiers="{ autoWidth: true, transparent: true, dots: true }"
                                button-type="tag"
                                aria-label="Options"
                                size="small"
                            />
                        </template>
                        <template #dropdown>
                            <div>
                                <slot name="secondaryActions" />
                            </div>
                        </template>
                    </BaseDropdown>
                </div>
            </div>
        </div>
        <BaseResourceImage
            v-if="hasArt"
            :resource="imageResource"
            :type="imageType"
            :radius="radius"
            :should-lazyload="false"
            :class="['image', { 'image-fill': fill }, { 'image-centered': centered }]"
        />
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.icon {
    &-container {
        @include fill;

        z-index: $layer-highlight-z-index;
        display: flex;
        align-items: center;
        justify-content: space-around;
        cursor: pointer;
        background: $c-frisky-channel-grad-transparent;
        opacity: 0;

        @include transition(opacity, slow);
    }

    &-play {
        @include square($s-xxl);

        z-index: $layer-tooltip-z-index;
        color: $c-white;
        border-radius: 50%;
    }
    &-spinning {
        height: 100%;
        line-height: 230px;
        opacity: 0.3;
        animation: spin var(--motion-spinner) linear infinite;
    }
}

// The idle play state: the old sprite's glyph carried its own white disc; the
// FDS glyph is the bare triangle, so the disc is chrome now.
.play-disc {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: $c-surface-chrome;
    background: $c-text-primary;
}

.action {
    width: $s-xxl;
    min-height: 1px;
    pointer-events: none;
    opacity: 0;

    @include transition(opacity, slow);
}

.base-resource-cover {
    position: relative;
    width: 100%;

    &-fill {
        padding-bottom: 100%;
        background: $c-classics-channel-grad;
    }

    &-centered {
        height: 100%;
        background: $c-classics-channel-grad;
    }

    &-playing {
        z-index: $layer-overlay-z-index;
        .icon-container {
            opacity: 1;
        }
    }

    // `.icon-container` is absolutely positioned (`@include fill`), so a radius
    // on the root alone would not round it, and `overflow: hidden` is not an
    // option — it would clip the options dropdown. The artwork itself is
    // rounded by forwarding `radius` to the nested BaseResourceImage, whose
    // own root clips it via `overflow: hidden`.
    &-radius-list {
        border-radius: $size-radius-artwork-sm;

        .icon-container {
            border-radius: $size-radius-artwork-sm;
        }
    }

    &-radius-card {
        border-radius: $size-radius-artwork;

        .icon-container {
            border-radius: $size-radius-artwork;
        }
    }

    &:hover {
        z-index: $layer-dropdown-z-index;

        .action {
            pointer-events: unset;
            opacity: 1;

            @include transition(opacity, base);
        }

        .icon-container {
            opacity: 1;

            @include transition(opacity, base);
        }
    }
}

.image {
    display: inline-block;
    max-width: 100%;

    &-fill {
        @include fill;
    }

    &-centered {
        min-width: 100%;
        min-height: 100%;
        object-fit: cover;

        @include center;
    }
}
</style>
